import { Component, Input, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { WidaPredictionDetails, WidaUsageLevels } from '../data/wida-prediction';
import Chart, { BubbleDataPoint } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'app-wida-widget',
  templateUrl: './wida-widget.component.html',
  styleUrls: ['./wida-widget.component.scss']
})

export class WidaWidgetComponent extends BaseComponent implements AfterViewChecked {
  @Input() widaPredictionDetails: WidaPredictionDetails;

  @ViewChild('widaGrowthChart') chartElement!: ElementRef<HTMLCanvasElement>;
  chart: Chart;
  chartInitialized: boolean = false;

  get notEnoughData(): boolean {
    return this.widaPredictionDetails?.ErrorCodesJson?.length > 0;
    // we previously had existence filtering earlier on but this is now a standalone widget
    // so we show not enough data for any error unless we want to make more specific messages.
    //return WidaPredictionDetails.notEnoughData(this.widaPredictionDetails);
  }

  get normalizedStartScore(): number { return 0 }
  get normalizedCurrentScore(): number {
    return this.getNormalizedScore(this.widaPredictionDetails.CurrentPredictedScore)
  }
  get normalizedNoUsageScore(): number { return 0.4 }
  get normalizedLowUsageScore(): number {
    return this.getNormalizedScore(this.widaPredictionDetails.FinalPredictedScoreLowUsage)
  }
  get normalizedMedUsageScore(): number {
    return this.getNormalizedScore(this.widaPredictionDetails.FinalPredictedScoreMediumUsage)
  }
  get normalizedHighUsageScore(): number { return 1 }

  readonly chartPointColor: string = '#03B1F2';
  readonly usageChartDetails: { [key in WidaUsageLevels]: ChartDetails } = {
    [WidaUsageLevels.NoUsage]: {
      bandColor: '#005781',
      legendLabel: '<15'
    },
    [WidaUsageLevels.LowUsage]: {
      bandColor: '#007EA8',
      legendLabel: '15-30'
    },
    [WidaUsageLevels.MedUsage]: {
      bandColor: '#00A6D1',
      legendLabel: '30-45'
    },
    [WidaUsageLevels.HighUsage]: {
      bandColor: '#9DDAE9',
      legendLabel: '>45'
    }
  }
  readonly chartCurrentScorePointFill: string = '#F78D2D';
  readonly chartCurrentScorePointStroke: string = '#FFC60B';

  ngAfterViewChecked(): void {
    this.showChart();
  }

  ngOnInit(): void {
    this.showChart();
  }

  getScoringSystemName(): string {
    if (this.widaPredictionDetails.ConvertedTo == "WIDA") {
      return "WIDA ACCESS"
    } else if (this.widaPredictionDetails.ConvertedTo == "TELPAS") {
      return "TELPAS";
    } else if (this.widaPredictionDetails.ConvertedTo == "ELPAC") {
      return "ELPAC";
    }
  }

  showChart() {
    if (!this.chartElement || this.chartInitialized) return;

    var numWeeks = 52;

    var weekLabels = new Array(numWeeks).fill('');
    weekLabels[0] = 'Start';
    weekLabels[numWeeks - 1] = 'After 1 year';

    var noUseData = this.initializeChartDataset(this.widaPredictionDetails.FinalPredictedScoreNoUsage, this.normalizedNoUsageScore);
    var lowUseData = this.initializeChartDataset(this.widaPredictionDetails.FinalPredictedScoreLowUsage, this.normalizedLowUsageScore);
    var medUseData = this.initializeChartDataset(this.widaPredictionDetails.FinalPredictedScoreMediumUsage, this.normalizedMedUsageScore);
    var highUseData = this.initializeChartDataset(this.widaPredictionDetails.FinalPredictedScoreHighUsage, this.normalizedHighUsageScore);

    var currentScoreData = Array.from({ length: numWeeks }, (_, i) => ({
      x: i,
      y: null,
      score: null
    }));
    const currentScoreIndex = this.weeksSinceMostRecentJulyFirstIndex();
    currentScoreData[currentScoreIndex] = {
      x: numWeeks - 1,
      y: this.normalizedCurrentScore,
      score: this.widaPredictionDetails.CurrentPredictedScore
    };

    this.chart = new Chart(this.chartElement.nativeElement, {
      type: 'line',
      data: {
        labels: weekLabels,
        datasets: [
          {
            label: this.usageChartDetails[WidaUsageLevels.HighUsage].legendLabel,
            data: highUseData,
            pointBackgroundColor: this.chartPointColor,
            backgroundColor: this.usageChartDetails[WidaUsageLevels.HighUsage].bandColor,
            fill: true,
            spanGaps: true,
            showLine: false,
            borderWidth: 0,
            pointRadius: 2,
            order: 5,
            datalabels: {
              align: function (context) {
                return (context.dataIndex === 0) ? 'top' : 'right';
              },
              formatter(value, _) { return value.score; }
            }
          },
          {
            label: this.usageChartDetails[WidaUsageLevels.MedUsage].legendLabel,
            data: medUseData,
            pointBackgroundColor: this.chartPointColor,
            backgroundColor: this.usageChartDetails[WidaUsageLevels.MedUsage].bandColor,
            fill: true,
            spanGaps: true,
            showLine: false,
            borderWidth: 0,
            pointRadius: 2,
            order: 4,
            datalabels: {
              display: function (context) {
                // label for first data point has already been drawn with dataset 1
                return context.dataIndex !== 0;
              },
              formatter(value, _) { return value.score; }
            }
          },
          {
            label: this.usageChartDetails[WidaUsageLevels.LowUsage].legendLabel,
            data: lowUseData,
            pointBackgroundColor: this.chartPointColor,
            backgroundColor: this.usageChartDetails[WidaUsageLevels.LowUsage].bandColor,
            fill: true,
            spanGaps: true,
            showLine: false,
            borderWidth: 0,
            pointRadius: 2,
            order: 3,
            datalabels: {
              display: function (context) {
                // label for first data point has already been drawn with dataset 1
                return context.dataIndex !== 0;
              },
              formatter(value, _) { return value.score; }
            }
          },
          {
            label: this.usageChartDetails[WidaUsageLevels.NoUsage].legendLabel,
            data: noUseData,
            pointBackgroundColor: this.chartPointColor,
            backgroundColor: this.usageChartDetails[WidaUsageLevels.NoUsage].bandColor,
            fill: true,
            spanGaps: true,
            showLine: false,
            borderWidth: 0,
            pointRadius: 2,
            order: 2,
            datalabels: {
              display: function (context) {
                // label for first data point has already been drawn with dataset 1
                return context.dataIndex !== 0;
              },
              formatter(value, _) { return value.score; }
            }
          },
          {
            label: '',
            data: currentScoreData,
            pointBackgroundColor: this.chartCurrentScorePointFill,
            pointBorderColor: this.chartCurrentScorePointStroke,
            pointRadius: 4,
            pointBorderWidth: 3,
            order: 1,
            datalabels: {
              display: false
            }
          },
        ],
      },
      options: {
        clip: 4,
        aspectRatio: 2.3,
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          title: {
            display: true,
            text: ['Projected ' + this.getScoringSystemName(), 'Score Over Time'],
            align: 'start',
            position: 'top',
            color: '#03B1F2',
            font: {
              family: 'Lato',
              weight: 600,
              size: 12
            },
            padding: {
              top: 0,
              bottom: 0
            }
          },
          legend: {
            display: false // custom html legend
          },
          tooltip: {
            filter: function (tooltipItem) {
              return tooltipItem?.datasetIndex === 4;
            },
            backgroundColor: 'white',
            borderColor: this.chartPointColor,
            borderWidth: 1,
            yAlign: 'bottom',
            caretPadding: 10,
            bodyAlign: 'center',
            bodyFont: {
              family: 'Lato',
              size: 20,
              weight: 600
            },
            bodyColor: '#212529',
            footerAlign: 'center',
            footerFont: {
              family: 'Lato',
              size: 12,
              weight: 200
            },
            footerColor: '#212529',
            footerMarginTop: 2,
            displayColors: false,
            padding: 6,
            callbacks: {
              footer: function (context) {
                return (context[0]?.datasetIndex === 4) ? 'Today\'s predicted score' : '';
              },
              label: function (context) {
                console.log("label context:");
                console.log(context);
                return (context.raw as CustomDataPoint).score.toString();
              }
            }
          },
          datalabels: {
            font: {
              family: 'Lato',
              size: 12,
              weight: 500
            },
            anchor: 'center',
            align: 'right',
            offset: 2,
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              autoSkip: false,
              font: {
                family: 'Lato',
                size: 12,
                weight: 500
              },
              minRotation: 0,
              maxRotation: 0
            }
          },
          y: {
            display: false,
            min: this.normalizedStartScore,
            max: Math.max(this.normalizedHighUsageScore, this.normalizedCurrentScore)
          }
        },
      },
      plugins: [ChartDataLabels, htmlLegendPlugin]
    });
    this.chartInitialized = true;
  }

  // helper function to get # weeks since July 1st to know 
  // at which index to display current WIDA prediction
  weeksSinceMostRecentJulyFirstIndex(): number {
    var today = new Date();
    // 0-indexed, so July is 6
    const lastJulyFirstYear = (today.getMonth() < 6) ? (today.getFullYear() - 1) : today.getFullYear();
    const lastJulyFirst = new Date(lastJulyFirstYear, 6, 1);

    const oneDay = 1000 * 60 * 60 * 24;
    const diffDays = Math.floor((today.getTime() - lastJulyFirst.getTime()) / oneDay);

    // the week difference logic would return 52 on the last day
    // (or two days, during leap years) of our defined school year,
    // but those two days aren't really worth a whole extra tick 
    // on our prediction graph, so we cap the (zero-indexed) number 
    // of weeks in a year at 51.
    const numWeeksZeroIndexed = 51;
    return Math.min(numWeeksZeroIndexed, Math.floor(diffDays / 7));
  }

  initializeChartDataset(finalScore: number, normalizedYValue: number): BubbleDataPoint[] {
    var numWeeks = 52;

    var chartData = Array.from({ length: numWeeks }, (_, i) => ({
      x: i,
      y: null,
      score: null
    }));
    chartData[0] = {
      x: 0,
      y: 0,
      score: this.widaPredictionDetails.StartingPredictedScore
    };
    chartData[numWeeks - 1] = {
      x: numWeeks - 1,
      y: normalizedYValue,
      score: finalScore
    };
    return chartData;
  }

  getNormalizedScore(score: number): number {
    var scoreIsHigherThanNoUsage = score > this.widaPredictionDetails.FinalPredictedScoreNoUsage;
    var min = scoreIsHigherThanNoUsage ?
      this.widaPredictionDetails.FinalPredictedScoreNoUsage :
      this.widaPredictionDetails.StartingPredictedScore;
    var max = scoreIsHigherThanNoUsage ?
      this.widaPredictionDetails.FinalPredictedScoreHighUsage :
      this.widaPredictionDetails.FinalPredictedScoreNoUsage;
    return scoreIsHigherThanNoUsage ?
      ((((score - min) / (max - min)) * (this.normalizedHighUsageScore - this.normalizedNoUsageScore))
        + this.normalizedNoUsageScore) :
      (((score - min) / (max - min)) * this.normalizedNoUsageScore);
  }
}

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const legendContainer = document.getElementById('legend-container');

    while (legendContainer.firstChild) {
      legendContainer.firstChild.remove();
    }

    const items = chart.options.plugins.legend.labels.generateLabels(chart);

    items.reverse().forEach(item => {
      if (item.text && item.text !== '') {
        const legendItem = document.createElement('div');
        legendItem.classList.add('chart-legend-item');
        legendItem.style.display = 'flex';
        legendItem.style.flexDirection = 'row';
        legendItem.style.alignItems = 'center';
        legendItem.style.marginBottom = '4px';

        const boxSize = '10px';
        const box = document.createElement('div');
        box.style.background = item.fillStyle;
        box.style.display = 'inline-block';
        box.style.flexShrink = '0';
        box.style.height = boxSize;
        box.style.width = boxSize;
        box.style.marginRight = '4px';

        const label = document.createElement('div');
        label.textContent = item.text;
        label.style.margin = '0';
        label.style.padding = '0';
        label.style.fontSize = '12px';

        legendItem.appendChild(box);
        legendItem.appendChild(label);
        legendContainer.appendChild(legendItem);
      }
    });

    const title = document.createElement('div');
    title.textContent = 'Minutes per week';
    title.style.fontSize = '10px';
    title.style.fontWeight = '600';
    title.style.textAlign = 'center';
    title.style.lineHeight = '1.2';
    title.style.marginTop = '4px';
    legendContainer.appendChild(title);
  }
};

export class ChartDetails {
  bandColor: string
  legendLabel: string
}

export class CustomDataPoint implements BubbleDataPoint {
  x: number
  y: number
  score: number
}